
import { RefreshCcwIcon, InfoIcon } from 'vue-feather-icons'
import ToastManager from '~/fc/ToastManager'
import { getStripeRemoteSubscription, getPaymentIntent } from '~/fc/Dal/PaymentRepo'
import AuthManager from '~/fc/Auth/AuthManager'
import { getUserTeam } from '~/fc/Dal/TeamRepo'
import { getUTCStandardDatetime } from '~/fc/Helpers/dateFormatter'
import { loadMoment } from '~/fc/ExternalScripts'
import { INTERRUPTED_JOBS_CHANNEL, PENDING_REMOTE_STATUSES } from '~/fc/constants'
import { sentTelemetryEvent } from '~/fc/TelemetryEvents'
import { priceWithCurrency } from '~/fc/PaymentHelper'

export default {
    head() {
        return {
            script: [
                {
                    src: 'https://cdnjs.cloudflare.com/ajax/libs/Chart.js/2.9.4/Chart.min.js'
                },
            ]
        }
    },
    components: {
        CombinedChart: () => import(/* webpackChunkName: "CombinedChart" */ "~/layouts/partials/account/report/combined"),
        Prompt: () => import(/* webpackChunkName: "Prompt" */ "~/components/ui/Prompt"),
        SubscriptionCancelPrompt: () => import(/* webpackChunkName: "Prompt" */ "~/components/ui/SubcriptionCancelPrompt"),
        RefreshCcwIcon,
        InfoIcon,
    },
    layout: "dashboard",
    data: () => ({
        reactivating: false,
        loading: false,
        subscriptions: [],
        showAll: true,
        loadingInProgress: true,
        minProgressLoading: true,
        inc: 1,
        isMember: false,
        teamInfo: {},
        showTeamMemberRestrictionAlert: false,
        moment: null,
        totalWebCpuMin: 0,
        totalApiCpuMin: 0,
        totalMin: 0,
        userHasUnifiedPackage: false,
        userHasWebPackage: false,
        userHasApiPackage: false,
        bannerDisplayed: false
    }),
    computed: {
        toastManager() { return new ToastManager(this) },
        authManager() { return new AuthManager(this) },
        user() { return this.$auth ? this.$auth.User : {} },
        canChange() {
            if (!this.teamInfo || !this.isMember) return true
            return this.isMember && this.teamInfo.owner === this.user.id;
        },
    },
    mounted() {
        this.loadMomentScript();
        this.getData();
        this.loadTeamInfo();

        if (localStorage.getItem('showResumeConversionAlert')) {
            // user comes from checkout.
            // now, check if there are any interrupted jobs on other tab
            const bc = new BroadcastChannel(INTERRUPTED_JOBS_CHANNEL);
            bc.postMessage('how_many_interrupted_jobs');
            bc.onmessage = (event) => {
                if (typeof event.data === "number") {
                    if (event.data && !this.bannerDisplayed) {
                        this.bannerDisplayed = true
                        this.notifyUserRegardingResumedConversion();
                    }
                    bc.close();
                }
            };
            localStorage.removeItem('showResumeConversionAlert');
        }
    },
    beforeDestroy() {
        this.$store.commit("setAlert", null);
    },
    methods: {
        loadMomentScript() {
            loadMoment((moment) => {                
                if (typeof moment !== 'function') {
                    setTimeout(() => {
                        this.loadMomentScript();
                    }, 100);
                } else {
                    this.moment = moment
                }
            });
        },
        notifyUserRegardingResumedConversion() {
            this.$store.commit("setAlert", {
                type: "success",
                msg: "Your pending conversions will auto-resume in your previous tab."
            });
        },
        async loadTeamInfo() {
            try {
                const { data } = await getUserTeam(this.$auth.Token);

                if (data && data[0] && data[0].currentUserRole !== "OWNER") {
                    this.isMember = true;
                    this.teamInfo = data[0].team;
                }
            } catch (e) { }
        },
        async getData() {
            if (this.loading) return;

            this.loadingInProgress = true;
            this.loading = true;
            this.minProgressLoading = true;
            this.subscriptions = [];

            try {
                // Get All Subscription
                const params = this.showAll ? { all: this.showAll } : {};
                const headers = {};

                // Add the country code to headers if it exists and is not 'US'
                const country = this.$store.state.store.country;
                if (country && country !== 'US') {
                    headers['fc-country-code'] = country;
                }

                // Make the API request
                const { data } = await this.$axios.get(`${process.env.PAYMENT_SERVICE}/subscriptions`, {
                    headers,
                    params,
                });

                // Parse data
                data.forEach(async (item) => {
                    if (
                        item.paymentMethod === "stripe" &&
                        (PENDING_REMOTE_STATUSES.includes(item.remoteStatus) ||
                            (item.remoteStatus === "active" && item.pendingUpgradeInfo))
                    ) {
                        if (item.planName && item.planName.toLowerCase().includes("api-credit")) {
                            const { data, status } = await getPaymentIntent(
                                this.$auth.Token,
                                item.remoteId
                            );
                            if (status === 200 && data.next_action?.use_stripe_sdk?.stripe_js) {
                                item.hosted_invoice_url =
                                    data.next_action.use_stripe_sdk.stripe_js;
                                item.payment_intent_status = data.status;
                            }
                        } else {
                            const { data, status } = await getStripeRemoteSubscription(
                                this.$auth.Token,
                                item.remoteId
                            );
                            if (status === 200 && data) {
                                item.hosted_invoice_url = data.latest_invoice?.hosted_invoice_url;
                                item.payment_intent_status =
                                    data.latest_invoice?.payment_intent?.status;
                            }
                        }
                    }
                    item.currentPeriodEnd = item.currentPeriodEnd
                        ? new Date(item.currentPeriodEnd)
                        : undefined;

                    if (PENDING_REMOTE_STATUSES.includes(item.remoteStatus)) {
                        item.useMin = "--/--";
                    }
                    this.subscriptions.push(item);
                });

                this.loadingInProgress = false;
                this.loading = false;

                const body = data.map((item) => item.id);

                const {
                    data: {
                        freeMinSubscription,
                        remainingMinutes,
                        usrMin,
                        userHasApiPackage,
                        userHasWebPackage,
                        userHasUnifiedPackage
                    },
                } = await this.$axios.post(
                    process.env.PROCESS_SERVICE + "/cpumin/user-subscription-status",
                    body
                );


                this.userHasUnifiedPackage = userHasUnifiedPackage;
                this.userHasApiPackage = userHasApiPackage
                this.userHasWebPackage = userHasWebPackage

                this.totalMin = usrMin.totalMin
                this.totalApiCpuMin = usrMin.userTotalApiAvailableMin;
                this.totalWebCpuMin = usrMin.userTotalWebAvailableMin;

                const remainingMinuteObj = {};
                // calculate available min
                remainingMinutes.forEach((item) => {
                    remainingMinuteObj[item.subscriptionId] = item;
                });

                data.forEach((item) => {
                    item.useMin = remainingMinuteObj[item.id]
                        ? remainingMinuteObj[item.id].remainingMinute +
                        "/" +
                        item.purchaseMinute
                        : 0 + "/" + item.purchaseMinute;
                });

                // free min data add
                freeMinSubscription.forEach((sub) => {
                    const freeUserSubscription = {
                        plan: {
                            name: "Free"
                        },
                        type: sub.paymentSubscriptionType.includes("web")
                            ? "web"
                            : "api",
                        currentPeriodEnd: new Date(sub.expireTime),
                        status: sub.status,
                        useMin:
                            sub.remainingMinute +
                            "/" +
                            sub.totalMinute,
                    };
                    freeUserSubscription.planName = freeUserSubscription.plan.name;
                    if (userHasApiPackage) {
                        freeUserSubscription.planName += " (Web)"
                    } else if (userHasWebPackage) {
                        freeUserSubscription.planName += " (Api)"
                    }
                    this.subscriptions.push(freeUserSubscription);
                });
            } catch (error) {
                if (error.response && error.response.status === 401) {
                    await this.authManager.logOut();
                }
                const message = error?.message || "Something went wrong";
                // Identify which API call caused the error
                if (error?.config?.url?.includes("subscriptions")) {
                    this.toastManager.add("danger", '', `${message}. Error: 1`);
                } else if (error?.config?.url?.includes("user-subscription-status")) {
                    this.toastManager.add("danger", '', `${message}. Error: 2`);
                } else {
                    this.toastManager.add("danger", '', `${message}. Error: 3`);
                }
            } finally {
                this.loadingInProgress = false;
                this.loading = false;
                this.minProgressLoading = false;
                this.inc++;
            }
        },
        formatStripeRemoteStatus(status) {
            switch (status) {
                case "requires_action":
                    return this.$t("stripe_status_requires_action");
                case "requires_payment_method":
                    return this.$t("stripe_status_requires_payment_method");
                case "processing":
                    return this.$t("stripe_status_processing");
                default:
                    return status;
            }
        },
        formatStripeStatusMsg(status) {
            switch (status) {
                case "requires_action":
                    return this.$t("stripe_msg_requires_action");
                case "requires_payment_method":
                    return this.$t("stripe_msg_requires_payment_method");
                case "processing":
                    return this.$t("stripe_msg_processing");
                default:
                    return this.$t("stripe_msg_default");
            }
        },
        getPricingLink(subscription) {
            if (subscription.type.includes("api")) {
                return this.localePath("/pricing#api");
            } else {
                return this.localePath("/pricing");
            }
        },
        isOneTime(subscription) {
            if (
                subscription.plan.name.includes("24-h") ||
                (subscription.plan.type &&
                    (subscription.plan.type.includes("api-credit") || subscription.plan.type.includes("unified-credit")))
            ) {
                return true;
            }
            return false;
        },
        nextRenewal(subscription) {
            const status = subscription.status;
            if (subscription.status === "inactive" && subscription.remoteStatus === 'trialing') {
                return (
                    "Starts " +
                    this.moment(subscription.currentPeriodEnd).format("DD MMM")
                );
            } else if (status === "inactive" || !subscription.currentPeriodEnd) {
                return "N/A";
            } else if (status === "active" && !subscription.cancelAtPeriodEnd) {
                if (this.isOneTime(subscription)) {
                    return (
                        "Cancels " +
                        this.moment(subscription.currentPeriodEnd).format("DD MMM")
                    );
                }
                return this.moment(subscription.currentPeriodEnd).format("DD MMM YYYY");
            } else if (status === "active" && subscription.cancelAtPeriodEnd) {
                return (
                    "Cancels " +
                    this.moment(subscription.currentPeriodEnd).format("DD MMM")
                );
            } else if (status === "pending") {
                if (this.isOneTime(subscription)) {
                    return (
                        "Cancels " +
                        this.moment(subscription.currentPeriodEnd).format("DD MMM")
                    );
                }
                return this.moment(subscription.currentPeriodEnd).format("DD MMM YYYY");
            }
        },
        nextRenewalUTC(subscription) {
            if (subscription.status === "inactive" && subscription.remoteStatus !== 'trialing') {
                return null;
            }
            if (!subscription.currentPeriodEnd) {
                return null;
            }
            const utc = getUTCStandardDatetime(subscription.currentPeriodEnd);

            const isOneTime =
                subscription.type === "web-onetime" ||
                subscription.type === "api-credit" || subscription.type === "unified-credit";

            if (subscription.status === "inactive" && subscription.remoteStatus === 'trialing') {
                return "Starts " + utc;
            }

            if (
                subscription.status === "pending" ||
                (subscription.status === "active" && !subscription.cancelAtPeriodEnd)
            ) {
                return isOneTime ? "Cancels " + utc : utc;
            }

            if (subscription.status === "active" && subscription.cancelAtPeriodEnd) {
                return "Cancels " + utc;
            }

            return utc;
        },
        getPrice(subscription) {
            if (
                subscription.pendingUpgradeInfo &&
                PENDING_REMOTE_STATUSES.includes(subscription.remoteStatus)
            ) {
                return subscription.pendingUpgradeInfo.pricingInfo.amount;
            }
            if (subscription.plan && subscription.plan.name && subscription.plan.name.toLowerCase().includes("free")) {
                return "0";
            } else if (subscription.plan.price.schema === "fixed") {
                return subscription.plan.price.amount;
            } else {
                let amount = 0;
                let minutes = parseInt(parseFloat(subscription.purchaseMinute) / 100);

                if (minutes == 10) {
                    minutes = 1;
                } // dirty fix for start
                subscription.plan.price.tiers.forEach((tier) => {
                    if (tier.start == minutes) {
                        if (minutes == 1) {
                            amount = (parseFloat(tier.amount) * 10).toFixed(2);
                        } else {
                            amount = (parseFloat(tier.amount) * minutes).toFixed(2);
                        }
                    }
                });
                return amount;
            }
        },
        getPriceText (subscription) {
            const price = this.getPrice(subscription)
            return priceWithCurrency(price, subscription.plan?.currency)
        },
        async onRenew(subscription) {
            try {
                if (this.reactivating) {
                    return;
                }
                this.reactivating = true;
                await this.$axios.get(
                    process.env.PAYMENT_SERVICE +
                    "/subscriptions/" +
                    subscription.id +
                    "/renew"
                );

                if (this.subscription.status === "active") {
                    this.$store.commit("setAlert", {
                        type: "success",
                        msg: "Your subscription was renew successfully.",
                    });
                } else {
                    this.$store.commit("setAlert", {
                        type: "error",
                        msg: "Payment Failed. Please check your payment method.",
                    });
                }
            } catch (error) {
                this.reactivating = false;
                this.$store.commit("setAlert", { type: "error", msg: error.message });
            }
            await this.getData();
        },
        /**
         * 
         * @param subscription 
         */
        async onReactivate(subscription) {
            try {
                if (this.reactivating) return;
                this.reactivating = true;
                const { data } = await this.$axios.get( process.env.PAYMENT_SERVICE + "/subscriptions/" + subscription.id + "/re-activate");
                if (data.status) {
                    this.$store.commit("setAlert", { type: "success", msg: "Your subscription was re-activated successfully." });
                    this.reactivating = false;
                    await this.getData();
                } else {
                    this.$store.commit("setAlert", { type: "error", msg: "Payment Failed. Please check your payment method." });
                }
            } catch (error) {
                this.reactivating = false;
                this.$store.commit("setAlert", { type: "error", msg: error.message });
            }
        },
        onCancel(subscription) {
            this.$refs.CancelWarning.open(subscription);
        },
        onAbort(subscription) {
            this.$refs.AbortWarning.open(subscription);
        },
        async cancel(subscription) {
            try {
                this.loaded = false;
                if (subscription.selected_reason) {
                    this.$gtm.push({ event: "subscription_cancel", reason: subscription.selected_reason });
                    sentTelemetryEvent('subscription_cancel', { reason: subscription.selected_reason })
                }
                await this.$axios.get(process.env.PAYMENT_SERVICE + "/subscriptions/" + subscription.id + "/cancel");
                await this.$axios.post(process.env.PAYMENT_SERVICE + "/subscriptions/user-subscription-cancel-feedback",
                    { selected_reason: subscription.selected_reason, additional_reason: subscription.additional_reason });
                await this.getData();

                this.$store.commit("setAlert", { type: "success", msg: "Your subscription is now canceled. Any un-used Conversion Minutes can be used until the plan's anniversary date." });
            } catch (error) {
                this.loaded = true;
                this.$store.commit("setAlert", { type: "error", msg: error.message });
            }
        },
        async abort(subscription) {
            try {
                this.loaded = false;
                await this.$axios.get(process.env.PAYMENT_SERVICE + "/subscriptions/" + subscription.id + "/abort");
                await this.getData();
                this.$store.commit("setAlert", { type: "success", msg: "Your pending subscription is now canceled" });
            } catch (error) {
                this.loaded = true;
                this.$store.commit("setAlert", { type: "error", msg: error.message });
            }
        },
        closeAlert(id) {
            document.getElementById(id).style.display = "none";
        },
    },
};
